import React, { Suspense, ComponentType } from 'react';
import LoadingSpinner from '../components/common/LoadingSpinner';

/**
 * Higher-order component for lazy loading with error boundary and loading states
 */
export function withLazyLoading(
  LazyComponent: React.LazyExoticComponent<any>,
  fallback?: React.ComponentType
) {
  const WrappedComponent = (props: any) => {
    const FallbackComponent = fallback || LoadingSpinner;
    
    return (
      <Suspense fallback={<FallbackComponent />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };

  WrappedComponent.displayName = `withLazyLoading(${(LazyComponent as any).displayName || 'Component'})`;
  
  return WrappedComponent;
}

/**
 * Preload a lazy component to improve perceived performance
 */
export function preloadComponent<T>(
  lazyComponent: React.LazyExoticComponent<ComponentType<T>>
): Promise<{ default: ComponentType<T> }> {
  // Try to access the internal payload, but fallback to calling the component
  try {
    const payload = (lazyComponent as any)._payload;
    if (payload && payload._result) {
      return Promise.resolve(payload._result);
    }
  } catch (error) {
    // Fallback: just call the lazy component to trigger loading
    console.warn('Could not access lazy component payload, using fallback');
  }
  
  // Fallback: trigger the lazy loading
  return (lazyComponent as any)._init((lazyComponent as any)._payload);
}

/**
 * Create a lazy component with retry logic for failed loads
 */
export function createRetryableLazy(
  importFn: () => Promise<{ default: any }>,
  maxRetries: number = 3
): React.LazyExoticComponent<any> {
  return React.lazy(() => {
    let retryCount = 0;
    
    const retry = async (): Promise<{ default: any }> => {
      try {
        return await importFn();
      } catch (error) {
        if (retryCount < maxRetries) {
          retryCount++;
          console.warn(`Failed to load component, retrying... (${retryCount}/${maxRetries})`);
          // Add exponential backoff
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
          return retry();
        }
        throw error;
      }
    };
    
    return retry();
  });
}

/**
 * Bundle loading states for different scenarios
 */
export const LoadingStates = {
  Page: () => (
    <div className="loading-page">
      <div className="loading-spinner-large">
        <div className="spinner"></div>
        <p>Loading page...</p>
      </div>
    </div>
  ),
  
  Component: () => (
    <div className="loading-component">
      <div className="loading-spinner-small">
        <div className="spinner"></div>
      </div>
    </div>
  ),
  
  Feature: ({ featureName }: { featureName: string }) => (
    <div className="loading-feature">
      <div className="loading-spinner-medium">
        <div className="spinner"></div>
        <p>Loading {featureName}...</p>
      </div>
    </div>
  ),
  
  Chart: () => (
    <div className="loading-chart">
      <div className="chart-skeleton">
        <div className="skeleton-bar"></div>
        <div className="skeleton-bar"></div>
        <div className="skeleton-bar"></div>
      </div>
    </div>
  )
};